import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Check, Sparkles } from 'lucide-react';
import './SignUpForm.css';
import { analytics } from '../utils/analytics';
import { toast } from 'react-toastify';

function SignUpForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industry: '',
    role: '',
    location: '',
    content_frequency: '',
    goal: '',
  });

  const totalSteps = 2;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (currentStep < totalSteps) {
      analytics.track('Form Step Completed', {
        step: currentStep,
        name_filled: !!formData.name,
        email_filled: !!formData.email,
      });
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      analytics.track('Form Step Back', {
        from_step: currentStep,
        to_step: currentStep - 1,
      });

      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    if (!formData.content_frequency || formData.content_frequency === '') {
      setError('Please select how often you want to post on LinkedIn');
      setLoading(false);
      return;
    }

    const validFrequencies = ['daily', 'recommended', 'moderate', 'weekly'];
    if (!validFrequencies.includes(formData.content_frequency)) {
      setError('Please select a valid posting frequency');
      setLoading(false);
      return;
    }

    // Track signup started
    analytics.track('Signup Started', {
      form_step: 'step_2_completed',
      industry: formData.industry,
      role: formData.role,
      content_frequency: formData.content_frequency,
    });

    console.log('Form submitted:', formData);

    try {
      const web3FormData = new FormData();
      web3FormData.append('access_key', '91bfa044-ccc8-44e7-add0-2e9ea7ba1370'); // Replace with your key
      web3FormData.append('name', formData.name);
      web3FormData.append('email', formData.email);
      web3FormData.append('industry', formData.industry);
      web3FormData.append('role', formData.role);
      web3FormData.append('location', formData.location);
      web3FormData.append('content_frequency', formData.content_frequency);
      web3FormData.append('goal', formData.goal);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: web3FormData,
      });

      const data = await response.json();

      if (data.success) {
        // Track successful signup
        analytics.track('Signup Completed', {
          email: formData.email,
          industry: formData.industry,
          role: formData.role,
          content_frequency: formData.content_frequency,
        });

        setIsSubmitted(true);
        toast.success('Successfully submitted! Check your email.');
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError('Failed to submit form. Please try again.');

      analytics.track('Signup Failed', {
        error: error.message,
        step: 'submission',
      });

      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isStep1Valid = formData.name && formData.email;
  const isStep2Valid =
    formData.industry &&
    formData.role &&
    formData.location &&
    formData.content_frequency &&
    formData.goal;

  if (isSubmitted) {
    return (
      <section className='signup-form-section' id='signup'>
        <div className='signup-container'>
          {/* Keep the main heading for context */}
          <div className='signup-header'>
            <h2 className='signup-title'>
              Get Your Personalized LinkedIn Content
            </h2>
          </div>

          {/* Success card in the same container style */}
          <div className='signup-form success-card'>
            <div className='success-icon-wrapper'>
              <div className='success-icon'>
                <Check size={48} />
              </div>
            </div>

            <h3 className='success-title'>You're on the list!</h3>
            <p className='success-subtitle'>
              Check your email for next steps. We'll send your first
              personalized content ideas within 24 hours.
            </p>

            {/* Next Steps Section */}
            <div className='success-next-steps'>
              <h4 className='next-steps-title'>What happens next?</h4>
              <div className='next-steps-list'>
                <div className='next-step-item'>
                  <div className='step-number'>1</div>
                  <div className='step-content'>
                    <strong>Check your email</strong>
                    <span>
                      We just sent you a confirmation to {formData.email}
                    </span>
                  </div>
                </div>
                <div className='next-step-item'>
                  <div className='step-number'>2</div>
                  <div className='step-content'>
                    <strong>Confirm your account</strong>
                    <span>
                      Click the link in the email to activate early access
                    </span>
                  </div>
                </div>
                <div className='next-step-item'>
                  <div className='step-number'>3</div>
                  <div className='step-content'>
                    <strong>Get your first ideas</strong>
                    <span>
                      Your first 3 personalized ideas arrive within 24 hours.
                      We'll keep sending fresh ideas throughout the week.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Proof */}
            {/* <div className='success-social-proof'>
              <p>
                <strong>Join 500+ professionals</strong> already creating
                amazing LinkedIn content with Story Engine
              </p>
            </div> */}

            {/* Optional CTA */}
            {/* <div className='success-cta'>
              <button
                className='btn-secondary-outline'
                onClick={() => (window.location.href = 'https://linkedin.com')}
              >
                Follow us on LinkedIn
              </button>
            </div> */}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='signup-form-section' id='signup'>
      <div className='signup-container'>
        <div className='signup-header'>
          <div className='signup-badge'>
            <Sparkles size={16} />
            <span>Start building today</span>
          </div>
          <h2 className='signup-title'>
            Create Your Professional Brand on LinkedIn
          </h2>
          <p className='signup-subtitle'>
            Share your expertise and goals. We'll match trending ideas to your
            profile and send them straight to your inbox.
          </p>
          {error && <div className='error-message'>{error}</div>}
        </div>

        <div className='progress-container'>
          <div className='progress-bar'>
            <div
              className='progress-fill'
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        <form
          className='signup-form'
          onSubmit={currentStep === totalSteps ? handleSubmit : handleNext}
        >
          {/* STEP 1 */}
          {currentStep === 1 && (
            <div className='form-step'>
              <h3 className='step-title'>Tell Us About Your Expertise</h3>
              <p className='step-description'>
                We'll analyze trending topics in your field and send
                personalized content ideas based on your profile
              </p>

              <div className='form-grid'>
                <div className='form-group full-width'>
                  <label className='form-label' htmlFor='name'>
                    Name
                  </label>
                  <input
                    type='text'
                    id='name'
                    name='name'
                    className='form-input'
                    value={formData.name}
                    onChange={handleChange}
                    placeholder='John Doe'
                    required
                  />
                </div>

                <div className='form-group full-width'>
                  <label className='form-label' htmlFor='email'>
                    Email
                  </label>
                  <input
                    type='email'
                    id='email'
                    name='email'
                    className='form-input'
                    value={formData.email}
                    onChange={handleChange}
                    placeholder='user@gmail.com'
                    required
                  />
                </div>
              </div>

              <button
                type='submit'
                className='form-submit form-submit-centered'
                disabled={!isStep1Valid}
              >
                Next
                <ArrowRight size={20} />
              </button>
            </div>
          )}

          {/* STEP 2 */}
          {currentStep === 2 && (
            <div className='form-step'>
              <h3 className='step-title'>
                Almost done! Let's personalize your content
              </h3>
              <p className='step-description'>
                This helps us create content ideas tailored to your industry and
                goals
              </p>

              <div className='form-grid'>
                <div className='form-group'>
                  <label className='form-label' htmlFor='industry'>
                    Industry
                  </label>
                  <input
                    type='text'
                    id='industry'
                    name='industry'
                    className='form-input'
                    value={formData.industry}
                    onChange={handleChange}
                    placeholder='e.g., SaaS, Fintech, Healthcare'
                    required
                  />
                </div>

                <div className='form-group'>
                  <label className='form-label' htmlFor='role'>
                    Your Role
                  </label>
                  <input
                    type='text'
                    id='role'
                    name='role'
                    className='form-input'
                    value={formData.role}
                    onChange={handleChange}
                    placeholder='e.g., Product Manager, Software Engineer'
                    required
                  />
                </div>

                <div className='form-group'>
                  <label className='form-label' htmlFor='location'>
                    Location
                  </label>
                  <input
                    type='text'
                    id='location'
                    name='location'
                    className='form-input'
                    value={formData.location}
                    onChange={handleChange}
                    placeholder='e.g., San Francisco, USA'
                    required
                  />
                </div>

                <div className='form-group'>
                  <label htmlFor='content_frequency' className='form-label'>
                    How often do you want to post on LinkedIn?
                  </label>
                  <select
                    name='content_frequency'
                    value={formData.content_frequency}
                    onChange={handleChange}
                    required
                    className='form-select'
                  >
                    <option value='' disabled>
                      Select frequency...
                    </option>
                    <option value='daily'>
                      Daily (5-7 posts/week) - Get 7 ideas
                    </option>
                    <option value='recommended'>
                      Recommended (3-5 posts/week) - Get 5 ideas
                    </option>
                    <option value='moderate'>
                      Moderate (2-3 posts/week) - Get 3 ideas
                    </option>
                    <option value='weekly'>
                      Weekly (Once per week) - Get 1 idea
                    </option>
                  </select>
                </div>

                <div className='form-group full-width'>
                  <label className='form-label' htmlFor='goal'>
                    What's your main LinkedIn goal?
                  </label>
                  <input
                    type='text'
                    id='goal'
                    name='goal'
                    className='form-input form-input-large'
                    value={formData.goal}
                    onChange={handleChange}
                    placeholder='e.g., Build my personal brand and increase visibility for better job opportunities'
                    required
                  />
                </div>
              </div>

              <div className='form-actions'>
                <button
                  type='button'
                  className='form-back'
                  onClick={handleBack}
                >
                  <ArrowLeft size={20} />
                  Back
                </button>

                <button
                  type='submit'
                  className='form-submit'
                  disabled={!isStep2Valid || loading}
                >
                  {loading ? 'Joining' : 'Get Early Access'}
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          )}

          <div className='form-benefits'>
            <div className='benefit-item'>
              <Check size={16} />
              <span>Free for Early Access</span>
            </div>
            <div className='benefit-item'>
              <Check size={16} />
              <span>No credit card required</span>
            </div>
            <div className='benefit-item'>
              <Check size={16} />
              <span>Cancel anytime</span>
            </div>
          </div>

          <p className='form-privacy'>
            We respect your privacy. Your data will never be shared.
          </p>
        </form>
      </div>
    </section>
  );
}

export default SignUpForm;
